/**
 * PageThumbnailRail
 * -----------------
 * Apple-Preview-style scrolling sidebar of page thumbnails.
 *
 * Performance notes
 * - The `file` prop accepts either a URL or a pdf.js `PDFDocumentProxy`. The
 *   parent loads the PDF once and passes the proxy here so the rail does NOT
 *   re-fetch or re-parse the file. (Loading a 30 MB PDF twice is the main
 *   reason older thumbnail rails feel "slow".)
 * - Each thumbnail is rendered lazily via IntersectionObserver: pages render
 *   to canvas only when scrolled into view, so 100-page PDFs don't pay the
 *   render cost up-front.
 * - Placeholder cards keep the scroll height stable so the observer triggers
 *   correctly and the scrollbar doesn't jump as cards materialise.
 *
 * Cost note
 * - This component does NOT touch Firestore or Storage. Comment and callout
 *   counts arrive pre-aggregated from the parent's existing subscriptions.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { MessageSquare, SquarePen, ChevronsLeft } from 'lucide-react';
import './PageThumbnailRail.css';

export interface PageCommentSummary {
  total: number;
  unread: number;
}

/**
 * Source the rail can render. Mirrors react-pdf's `File` shape but trimmed to
 * the variants we actually pass in: a URL string or an already-fetched binary
 * buffer (so we don't pay the network cost twice).
 */
export type ThumbnailRailSource =
  | string
  | { data: ArrayBuffer }
  | { url: string }
  | null;

export interface PageThumbnailRailProps {
  file: ThumbnailRailSource;
  numPages: number;
  currentPage: number;
  onSelectPage: (page: number) => void;
  /** Map keyed by 1-based page number. */
  commentCounts?: Map<number, PageCommentSummary>;
  /** Callout text-box counts keyed by 1-based page number. */
  calloutCounts?: Map<number, number>;
  visible: boolean;
  onCollapse?: () => void;
  /** height / width — used to size placeholder cards before each page renders. */
  naturalAspect?: number;
}

const THUMB_WIDTH = 124;
const DEFAULT_ASPECT = 1.294; // US Letter portrait fallback

function thumbAriaLabel(pageNum: number, counts: PageCommentSummary | undefined, calloutCount: number): string {
  const parts = [`Page ${pageNum}`];
  if (counts?.total) parts.push(`${counts.total} comment${counts.total === 1 ? '' : 's'}`);
  if (calloutCount > 0) parts.push(`${calloutCount} engineer note${calloutCount === 1 ? '' : 's'}`);
  return parts.join(' — ');
}

interface ThumbItemProps {
  pageNum: number;
  isActive: boolean;
  counts: PageCommentSummary | undefined;
  calloutCount?: number;
  onSelect: () => void;
  registerRef: (el: HTMLButtonElement | null) => void;
  /** Root element used by IntersectionObserver to gate rendering. */
  scrollRoot: HTMLElement | null;
  placeholderHeight: number;
}

const ThumbItem: React.FC<ThumbItemProps> = ({
  pageNum,
  isActive,
  counts,
  calloutCount = 0,
  onSelect,
  registerRef,
  scrollRoot,
  placeholderHeight,
}) => {
  const wrapperRef = useRef<HTMLButtonElement | null>(null);
  // Once a thumbnail has been seen, keep it rendered. The CPU cost of canvas
  // re-rasterisation on each visibility flip is bigger than the memory cost
  // of keeping the canvas around.
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (shouldRender) return;
    const el = wrapperRef.current;
    if (!el || !scrollRoot) return;
    const io = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShouldRender(true);
            io.disconnect();
            break;
          }
        }
      },
      {
        root: scrollRoot,
        // Render slightly before scroll arrives so users don't see blank cards.
        rootMargin: '300px 0px 600px 0px',
        threshold: 0.01,
      },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [scrollRoot, shouldRender]);

  return (
    <button
      ref={el => {
        wrapperRef.current = el;
        registerRef(el);
      }}
      type="button"
      className={`pdf-thumb-item ${isActive ? 'pdf-thumb-item--active' : ''}`}
      onClick={onSelect}
      aria-current={isActive ? 'page' : undefined}
      aria-label={thumbAriaLabel(pageNum, counts, calloutCount)}
    >
      <div
        className="pdf-thumb-canvas-wrap"
        style={{ width: THUMB_WIDTH, minHeight: placeholderHeight }}
      >
        {shouldRender ? (
          <Page
            pageNumber={pageNum}
            width={THUMB_WIDTH}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            className="pdf-thumb-page"
            loading={<div className="pdf-thumb-skeleton" style={{ height: placeholderHeight }} />}
            error={<div className="pdf-thumb-skeleton pdf-thumb-skeleton--err" style={{ height: placeholderHeight }} />}
          />
        ) : (
          <div className="pdf-thumb-skeleton" style={{ height: placeholderHeight }} />
        )}
        {(calloutCount > 0 || (counts && counts.total > 0)) && (
          <div className="pdf-thumb-badges">
            {calloutCount > 0 && (
              <span
                className="pdf-thumb-badge pdf-thumb-badge--callout"
                title={`${calloutCount} engineer note${calloutCount === 1 ? '' : 's'}`}
              >
                <SquarePen size={10} strokeWidth={2.4} />
                <span className="pdf-thumb-badge-num">{calloutCount}</span>
              </span>
            )}
            {counts && counts.total > 0 && (
              <span
                className={`pdf-thumb-badge ${counts.unread > 0 ? 'pdf-thumb-badge--unread' : ''}`}
                title={
                  counts.unread > 0
                    ? `${counts.total} comment${counts.total === 1 ? '' : 's'} · ${counts.unread} unread`
                    : `${counts.total} comment${counts.total === 1 ? '' : 's'}`
                }
              >
                <MessageSquare size={10} strokeWidth={2.4} />
                <span className="pdf-thumb-badge-num">{counts.total}</span>
                {counts.unread > 0 && <span className="pdf-thumb-badge-dot" aria-hidden="true" />}
              </span>
            )}
          </div>
        )}
      </div>
      <span className="pdf-thumb-label">{pageNum}</span>
    </button>
  );
};

const PageThumbnailRail: React.FC<PageThumbnailRailProps> = ({
  file,
  numPages,
  currentPage,
  onSelectPage,
  commentCounts,
  calloutCounts,
  visible,
  onCollapse,
  naturalAspect,
}) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  // We need to force a re-render once the scroller mounts so children can
  // hand its element to their IntersectionObservers.
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);

  const placeholderHeight = Math.round(THUMB_WIDTH * (naturalAspect || DEFAULT_ASPECT));

  const pageList = useMemo(() => {
    const out: number[] = [];
    for (let i = 1; i <= numPages; i++) out.push(i);
    return out;
  }, [numPages]);

  useEffect(() => {
    if (!visible) return;
    const el = itemRefs.current.get(currentPage);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [currentPage, visible]);

  if (!visible) return null;

  return (
    <aside className="pdf-thumb-rail" aria-label="Page thumbnails">
      <div className="pdf-thumb-rail-header">
        <span className="pdf-thumb-rail-title">Pages</span>
        <span className="pdf-thumb-rail-count">{numPages || '—'}</span>
        {onCollapse && (
          <button
            type="button"
            className="pdf-thumb-rail-collapse"
            onClick={onCollapse}
            title="Hide thumbnails"
            aria-label="Hide thumbnails"
          >
            <ChevronsLeft size={14} />
          </button>
        )}
      </div>
      <div
        className="pdf-thumb-rail-scroller"
        ref={el => {
          scrollerRef.current = el;
          setScrollRoot(el);
        }}
      >
        {file && numPages > 0 ? (
          <Document
            file={file}
            loading={<div className="pdf-thumb-rail-loading">Loading…</div>}
            error={<div className="pdf-thumb-rail-loading">Failed to load</div>}
            noData={<div className="pdf-thumb-rail-loading">No PDF</div>}
          >
            {pageList.map(pageNum => (
              <ThumbItem
                key={pageNum}
                pageNum={pageNum}
                isActive={pageNum === currentPage}
                counts={commentCounts?.get(pageNum)}
                calloutCount={calloutCounts?.get(pageNum) ?? 0}
                onSelect={() => onSelectPage(pageNum)}
                registerRef={el => {
                  if (el) itemRefs.current.set(pageNum, el);
                  else itemRefs.current.delete(pageNum);
                }}
                scrollRoot={scrollRoot}
                placeholderHeight={placeholderHeight}
              />
            ))}
          </Document>
        ) : (
          <div className="pdf-thumb-rail-loading">Preparing pages…</div>
        )}
      </div>
    </aside>
  );
};

export default PageThumbnailRail;
